import { buildImgLink, MEDIA_BUCKET_NAME, STORAGE_RELATED_PREVIEW_TYPE } from '../../../services/mediaStore';
import {
    ACCEPT_IMG_FILE_FORMATS,
    BYTE_FRACTION_DIGITS,
    BYTE_SIZE_UNITS,
    BYTES_IN_UNIT,
    CARD_ELEVATION,
    DEFAULT_STORAGE_LIMIT_LABEL,
    ORDER_SORTING_VALUES,
    PAGINATION_FIRST_PAGE_VALUE,
    PAGINATION_PER_PAGE_VALUE,
    PAGINATION_STEP,
    SELECTED_CARD_ELEVATION,
    STORAGE_LIMIT_PLACEHOLDER,
    TIME_INTERVALS,
} from '../constants';
import {
    EZLOGIC_TITLE_CLOUD_SPACE_INFO,
    EZLOGIC_TITLE_STORAGE_LIMIT_INFO_TEXT,
    EZLOGIC_TITLE_YOUR_CLOUD_SPACE,
} from '../../../constants/language_tokens';
import { PAGINATION_ARROWS } from 'constants/App';
import { getFileExtension } from '../../../services/mediaStore/src/utils';

/**
 * Removes the specified query parameters from the url
 * @param {string} url - any url
 * @param {string[]} paramsToRemove - Query list of parameters to remove from the URL
 * @returns {string} new url without params (https://download-cloud.ezlo.com?uuid=1)
 * @example
 * removeParamsFromUrl('https://download-cloud.ezlo.com?uuid=1&redirect_support=1', ['redirect_support'])
 */
export function removeParamsFromUrl(url, paramsToRemove) {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.search);

    paramsToRemove.forEach((param) => {
        if (searchParams.has(param)) {
            searchParams.delete(param);
        }
    });

    urlObj.search = searchParams.toString();

    return urlObj.toString();
}

/**
 * Builds a link image in the desired format for copying
 * @param {string} url - image url
 * @returns {string} new url for copy (https://download-cloud.ezlo.com?uuid=1) without redirect_support
 * @example
 * buildUrlForCopy('https://download-cloud.ezlo.com?uuid=1&redirect_support=1')
 */
export const buildUrlForCopy = (url) => {
    return removeParamsFromUrl(url, ['redirect_support']);
};

/**
 * Collects the uuids of all preview (thumbnail) files referenced by other files in the list.
 * The backend returns a separate thumbnail entry per uploaded image, linked from the original
 * file via `storage_related.list[].storage_file_uuid` (type 'preview'); such thumbnails must not
 * be shown as standalone gallery items.
 * @param {Array<Object>} imgListFromResponse - list of images from response of cloud request
 * @returns {Set<string>} set of preview file uuids
 * @example
 * getPreviewUuids([{ storage_related: { list: [{ type: 'preview', storage_file_uuid: '1' }] } }])
 */
export const getPreviewUuids = (imgListFromResponse) => {
    const previewUuids = new Set();

    imgListFromResponse.forEach((image) => {
        image?.storage_related?.list?.forEach((relatedFile) => {
            if (relatedFile?.type === STORAGE_RELATED_PREVIEW_TYPE && relatedFile?.storage_file_uuid) {
                previewUuids.add(relatedFile.storage_file_uuid);
            }
        });
    });

    return previewUuids;
};

/**
 * Builds a list of images in the format required by the UI.
 * Preview (thumbnail) files are excluded so each uploaded image is shown only once.
 * @param {Array<Object>} imgListFromResponse - list of images from response of cloud request
 * @returns {Array<Object>} list of images in the format required by the UI [{{uuid: '1', filename:'logo.png', timestamp: 1236, link: 'https://download-cloud.ezlo.com?uuid=1&redirect_support=1'}}]
 * @example
 * buildImagesList([{uuid: '1', filename:'logo.png', timestamp: 1236, file_format: png}])
 */
export function buildImagesList(imgListFromResponse) {
    const previewUuids = getPreviewUuids(imgListFromResponse);

    return imgListFromResponse
        .filter((response) =>
            Boolean(
                response?.uuid &&
                    response?.key &&
                    !previewUuids.has(response.uuid) &&
                    ACCEPT_IMG_FILE_FORMATS.includes(response.file_format),
            ),
        )
        .map(({ uuid, key, timestamp, file_format, filename }) => ({
            link: buildImgLink({ uuid, file_format, key }),
            uuid,
            timestamp,
            filename,
        }));
}

/**
 * Function build filters for fetch images request depending on the input parameters
 * @param {'today' | 'week' | 'year' | 'month' | 'all_time'} param - time interval param
 * @param {number} page - page number
 * @param {number} per_page - per_page number
 * @returns {Object} filters for fetch images request depending on the input parameters
 * @example
 * getFetchImagesFilter('today', 3, 25)
 */
export function getFetchImagesFilter(param, page = PAGINATION_FIRST_PAGE_VALUE, per_page = PAGINATION_PER_PAGE_VALUE) {
    const currentTime = new Date();

    const filters = {
        page: page,
        per_page: per_page,
    };

    if (param === TIME_INTERVALS.TODAY) {
        const todayStart = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate());
        filters.timestamp_after = todayStart.getTime();
    } else if (param === TIME_INTERVALS.WEEK) {
        const weekAgo = new Date(currentTime.getTime() - 7 * 24 * 60 * 60 * 1000);
        filters.timestamp_after = weekAgo.getTime();
    } else if (param === TIME_INTERVALS.MONTH) {
        const monthAgo = new Date(currentTime.getFullYear(), currentTime.getMonth() - 1, currentTime.getDate());
        filters.timestamp_after = monthAgo.getTime();
    } else if (param === TIME_INTERVALS.YEAR) {
        const yearAgo = new Date(currentTime.getTime() - 365 * 24 * 60 * 60 * 1000);
        filters.timestamp_after = yearAgo.getTime();
    }

    return filters;
}

/**
 * Function adds uuid to the list if it is not in the list and removes uuid from the list if it is there
 * @param {string} uuid - image uuid
 * @param {string[]} selectedImgUuids - list of selected image uuids
 * @returns {Array<Object>} new selected images uuid list
 * @example
 * toggleSelectedImgUuids('1', ['2', '10', '1'])
 */
export const toggleSelectedImgUuids = (uuid, selectedImgUuids) => {
    return selectedImgUuids.includes(uuid)
        ? selectedImgUuids.filter((imgUuid) => imgUuid !== uuid)
        : [...selectedImgUuids, uuid];
};

/**
 * Function sorts the list of images depending on order parameter using 'timestamp'
 * @param {Array<Object>} images - list of images
 * @param {string} order - pagination param ('new' or 'old')
 * @returns {Array<Object>} sorted list of images
 * @example
 * sortImagesByTimestamp([{uuid: '1', filename:'logo.png', timestamp: 1236}], 'new')
 */
export function sortImagesByTimestamp(images, order) {
    if (order === ORDER_SORTING_VALUES.NEW) {
        return [...images].sort((a, b) => b.timestamp - a.timestamp);
    }

    if (order === ORDER_SORTING_VALUES.OLD) {
        return [...images].sort((a, b) => a.timestamp - b.timestamp);
    }

    return images;
}

/**
 * Determines, depending on pagination param, what the new page number should be
 * @param {number} currentPage - current page number
 * @param {string} param - pagination param ('next' or 'prev')
 * @returns {number} new page number
 * @example
 * getPageByPaginationParam(2, 'next')
 */
export const getPageByPaginationParam = (currentPage, param) => {
    if (param === PAGINATION_ARROWS.NEXT) {
        return currentPage + PAGINATION_STEP;
    }

    if (param === PAGINATION_ARROWS.PREV) {
        return currentPage - PAGINATION_STEP;
    }

    return currentPage;
};

/**
 * Depending on isSelectedImg, the function returns elevation number for the card
 * @param {boolean} isSelectedImg - Indicates whether the image is selected
 * @returns {number} elevation number
 * @example
 * getCardElevation(true)
 */
export const getCardElevation = (isSelectedImg) => {
    return isSelectedImg ? SELECTED_CARD_ELEVATION : CARD_ELEVATION;
};

/**
 * Determines if uuid is in the list of selected uuids
 * @param {string} uuid - image uuid
 * @param {string[]} selectedImgUuids - list of selected image uuids
 * @returns {boolean} will return true if the uuid is in the list of selected uuids
 * @example
 * determineIsSelectedImg('2sfr53', ['485sdf6', '2sfr53', '4125sfe8'])
 */
export const determineIsSelectedImg = (uuid, selectedImgUuids) => {
    return selectedImgUuids.includes(uuid);
};

/**
 * Provides data from the redux store
 * @param {Object} - redux store
 * @returns {Object} data from redux store
 * @example
 * useSelector(mediaStoreSelector)
 */
export const mediaStoreSelector = ({ app, mediaStore }) => ({
    isLoading: app.lineLoading,
    imagesList: mediaStore.imagesList,
    uploadingImages: mediaStore.uploadingImages,
    isEndPage: mediaStore.isEndPage,
});

/**
 * Build img in ui format
 * @param {Object} responseFile - uploaded file response
 * @param {File} file - uploaded file
 * @returns {Object} img in ui format
 * @example
 * buildNewImageInUiFormat({uuid: '1', key: '524jg'}, {name: 'logo.png, ...other})
 */
export const buildNewImageInUiFormat = (responseFile, file) => {
    return {
        ...responseFile,
        filename: file.name,
        timestamp: new Date().getTime(),
        file_format: getFileExtension(file),
    };
};

/**
 * Formats a number of bytes into a human-readable string (decimal, base 1000)
 * @param {number|string} bytes - number of bytes
 * @returns {string} formatted size, e.g. '100 MB'
 * @example
 * formatBytes(1000000) // '1 MB'
 */
export const formatBytes = (bytes) => {
    const safeBytes = Number(bytes);

    if (!safeBytes || safeBytes < 0) {
        return `0 ${BYTE_SIZE_UNITS[0]}`;
    }

    const unitIndex = Math.min(Math.floor(Math.log(safeBytes) / Math.log(BYTES_IN_UNIT)), BYTE_SIZE_UNITS.length - 1);
    const value = safeBytes / Math.pow(BYTES_IN_UNIT, unitIndex);

    return `${Number(value.toFixed(BYTE_FRACTION_DIGITS))} ${BYTE_SIZE_UNITS[unitIndex]}`;
};

/**
 * Extracts media bucket usage (used/total bytes) from a 'storage_status' response data.
 * Aggregates the 'media' bucket across all account locations.
 * @param {Object} storageStatusData - 'data' field of the storage_status response ({ locations })
 * @returns {{used: number, total: number}|null} media storage usage in bytes, or null when no
 * media bucket is present (no data to show) so the UI can fall back to the default storage info text
 * @example
 * getMediaStorageUsage({ locations: { id: { media: { total: '0', max: '100' } } } })
 */
export const getMediaStorageUsage = (storageStatusData) => {
    const locations = Object.values(storageStatusData?.locations ?? {});
    const mediaBuckets = locations.map((location) => location?.[MEDIA_BUCKET_NAME]).filter(Boolean);

    if (!mediaBuckets.length) {
        return null;
    }

    return mediaBuckets.reduce(
        (usage, mediaBucket) => ({
            used: usage.used + Number(mediaBucket.total ?? 0),
            total: usage.total + Number(mediaBucket.max ?? 0),
        }),
        { used: 0, total: 0 },
    );
};

/**
 * Provides the cloud media storage usage ({used, total} in bytes, or null) from the redux store
 * @param {Object} - redux store
 * @returns {{used: number, total: number}|null} storage usage from the redux store
 * @example
 * useSelector(storageUsageSelector)
 */
export const storageUsageSelector = ({ mediaStore }) => mediaStore.storageUsage;

/**
 * Builds the Media Storage info texts from the current cloud usage.
 * The tooltip limit and the "Your cloud space" value reflect the data from the request,
 * falling back to the default texts while there is no data.
 * @param {{used: number, total: number}|null} storageUsage - current cloud media usage in bytes
 * @param {function} t - translation function
 * @returns {{cloudSpaceText: string, storageLimitInfoText: string}} info texts for the UI
 * @example
 * getStorageInfoText({ used: 1000000, total: 5000000000 }, t)
 */
export const getStorageInfoText = (storageUsage, t) => {
    const limitLabel = storageUsage ? formatBytes(storageUsage.total) : DEFAULT_STORAGE_LIMIT_LABEL;
    const storageLimitInfoText = t(EZLOGIC_TITLE_STORAGE_LIMIT_INFO_TEXT).replace(
        STORAGE_LIMIT_PLACEHOLDER,
        limitLabel,
    );
    const cloudSpaceText = storageUsage
        ? `${t(EZLOGIC_TITLE_YOUR_CLOUD_SPACE)}: ${formatBytes(storageUsage.used)} / ${formatBytes(storageUsage.total)}`
        : t(EZLOGIC_TITLE_CLOUD_SPACE_INFO);

    return { cloudSpaceText, storageLimitInfoText };
};
